import { useQuery } from "@tanstack/react-query";
import { type ComponentType, useMemo } from "react";

import { api } from "../../api/client";
import { useUi } from "../../state/ui";
import { revealPaint, revealStyle, useFadeIn, useMapRevealed } from "../reveal";
import { FRIEND_BLUE } from "../units/symbols";
import { useDeployment } from "../units/useDeployment";
import { type AirspaceKind, airspaceKey, INACTIVE_OPACITY, shownAirspace } from "./dronesFeatures";

/** Calques du groupe « Drones » (chantier « Drones », D11 et D13, exception accordée à la carte figée) : volumes
 *  aériens alloués (VAA), ROZ drones et zones d'intérêt du renseignement (NAI) du dossier, calculés par le serveur sur
 *  son état courant (`GET /exercises/{id}/drones/airspace`, sans OPO), dessinés sous les graphiques tactiques :
 *  - VAA en bleu ami, trait plein de 1,25 px, remplissage léger ;
 *  - ROZ drones en orange des volumes aériens, tireté [6, 3] de 1,75 px ;
 *  - NAI en bleu ami, tireté court [2, 2] de 1,5 px, sans remplissage ;
 *  - étiquettes en bloc de texte (nom, volume, créneau) ; ce qui n'est pas actif au temps affiché passe à 35 %.
 *  Décochés par défaut ; chaque ligne cochée monte en fondu. Rien en CENTAC (collection vide, aucune requête). */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

/** Orange des volumes aériens (ROZ), celui des cartes du corps. */
export const ROZ_ORANGE = "#D66E00";
const BELOW = "graphics-fill";
const FADE_MS = 450;

/** Couleur de chaque nature d'élément. */
export const KIND_COLOUR: Readonly<Record<AirspaceKind, string>> = { vaa: FRIEND_BLUE, roz: ROZ_ORANGE, nai: FRIEND_BLUE };

/** Calque du dossier ouvert (lu même calques décochés : compteurs du panneau) ; rien avant la création du dossier ni
 *  en CENTAC. */
export function useDroneAirspace(enabled = true) {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: deployment } = useDeployment();
  const revealed = useMapRevealed();
  const centac = Boolean(deployment?.centac) || deployment?.level === "CENTAC";
  const version = deployment?.exercise_version ?? null;
  return useQuery({
    queryKey: airspaceKey(exerciseId, version, null),
    queryFn: () => api.droneAirspace(exerciseId as string),
    enabled: enabled && revealed && Boolean(exerciseId && deployment) && !centac,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Zoom sous lequel les étiquettes se réduisent au nom (volume et créneau dans l'info-bulle) : vue d'ensemble lisible. */
export const DETAIL_ZOOM = 10;

/** Bloc de texte d'un élément : nom en gras, volume et créneau dessous (`compact` : le nom seul). */
function AirspaceTag({ kind, label, lines, compact }: { kind: AirspaceKind; label: string; lines: string[]; compact: boolean }) {
  return (
    <div className="pointer-events-none flex flex-col items-start rounded-md border border-line-strong bg-surface px-1.5 py-0.5 font-sans shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
      <span className="font-mono text-[11px] leading-tight font-bold whitespace-nowrap" style={{ color: KIND_COLOUR[kind] }}>
        {label}
      </span>
      {!compact &&
        lines.map((line) => (
        <span key={line} className="font-mono text-[10px] leading-tight whitespace-nowrap text-ink-soft tabular-nums">
          {line}
        </span>
      ))}
    </div>
  );
}

export function DronesLayers({ Source, Layer, Marker, zoom = DETAIL_ZOOM }: EngineParts & { zoom?: number }) {
  const vaa = useUi((s) => s.controlVisible.droneVaa === true);
  const roz = useUi((s) => s.controlVisible.droneRoz === true);
  const nai = useUi((s) => s.controlVisible.droneNai === true);
  const phase = useUi((s) => s.phase);
  const any = vaa || roz || nai;
  const { data } = useDroneAirspace(any);
  const built = useMemo(() => shownAirspace(data, { vaa, roz, nai }, phase), [data, vaa, roz, nai, phase]);
  // Nouvelle ligne cochée ou nouveau calcul : montée en fondu.
  const shown = useFadeIn(any && data ? `${vaa}${roz}${nai}-${data.features.length}` : null);
  if (!any || !data || !built.areas.features.length) return null;
  const opacity = (value: number) => ["case", ["get", "active"], value, value * INACTIVE_OPACITY];
  const colour = ["match", ["get", "kind"], "roz", ROZ_ORANGE, FRIEND_BLUE];
  return (
    <>
      <Source id="gx-drone-airspace" type="geojson" data={built.areas}>
        <Layer
          id="drone-airspace-fill"
          type="fill"
          beforeId={BELOW}
          filter={["!=", ["get", "kind"], "nai"]}
          paint={revealPaint("fill", { "fill-color": colour, "fill-opacity": ["match", ["get", "kind"], "roz", opacity(0.06), opacity(0.04)] }, shown, FADE_MS)}
        />
        <Layer
          id="drone-vaa-line"
          type="line"
          beforeId={BELOW}
          filter={["==", ["get", "kind"], "vaa"]}
          layout={{ "line-join": "round" }}
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.25, "line-opacity": opacity(1) }, shown, FADE_MS)}
        />
        <Layer
          id="drone-roz-line"
          type="line"
          beforeId={BELOW}
          filter={["==", ["get", "kind"], "roz"]}
          layout={{ "line-join": "round" }}
          paint={revealPaint("line", { "line-color": ROZ_ORANGE, "line-width": 1.75, "line-dasharray": [6, 3], "line-opacity": opacity(1) }, shown, FADE_MS)}
        />
        <Layer
          id="drone-nai-line"
          type="line"
          beforeId={BELOW}
          filter={["==", ["get", "kind"], "nai"]}
          layout={{ "line-join": "round" }}
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.5, "line-dasharray": [2, 2], "line-opacity": opacity(1) }, shown, FADE_MS)}
        />
      </Source>
      {built.labels.map((label) => (
        <Marker
          key={label.key}
          longitude={label.at[0]}
          latitude={label.at[1]}
          anchor="center"
          style={{ pointerEvents: "none" }}
        >
          <div
            style={{ ...revealStyle(shown, FADE_MS), ...(shown && !label.active ? { opacity: INACTIVE_OPACITY } : {}) }}
          >
            <AirspaceTag kind={label.kind} label={label.label} lines={label.lines} compact={zoom < DETAIL_ZOOM} />
          </div>
        </Marker>
      ))}
    </>
  );
}
